import { LeftOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Drawer, Modal, notification, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useUpdateMapping } from '../../../../../utils/api/mapping/useUpdateMapping';
import { PRODUCT_TRANSLATION_FIELDS } from '../../../../../utils/const/mapping';
import { updateMappingWithField } from '../../../../../utils/fn/mapping';
import {
  useMappingDetails,
  useMappingForm,
  useSelectedField,
} from '../../../../../utils/zustand';
import MappingForm from './MappingForm';

const MappingConfigurator = ({ triggerOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const { selectedField, clearSelectedField } = useSelectedField();
  const { mappingDetails } = useMappingDetails();
  const { updateMapping, isLoading: isUpdating } = useUpdateMapping();
  const { validateForm } = useMappingForm();

  const handleClose = () => {
    clearSelectedField();
    setIsOpen(false);
  };

  const findTranslateDbFields = (selectedField) => {
    const getDbFieldIfTranslate = (transformation) =>
      transformation?.type === 'translate' &&
      PRODUCT_TRANSLATION_FIELDS.includes(transformation?.source_field)
        ? transformation.source_field
        : null;

    if (selectedField?.transformation_type === 'unique') {
      const matchedField = getDbFieldIfTranslate(
        selectedField.transformation?.unique?.transformation
      );
      return matchedField ? [matchedField] : [];
    }

    if (selectedField?.transformation_type === 'chain') {
      const chain = selectedField.transformation?.chain;
      const matchedDefaultField = getDbFieldIfTranslate(
        chain?.default_transformation
      );
      const matchedStepFields = (chain?.steps || [])
        .map((step) => getDbFieldIfTranslate(step.transformation))
        .filter(Boolean);
      return [
        ...(matchedDefaultField ? [matchedDefaultField] : []),
        ...matchedStepFields,
      ];
    }

    return [];
  };

  // TODO: export this and handle form errors.
  const handleSave = async () => {
    // 1. VALIDATION DU FORMULAIRE (Form.Item)
    const { success: formValid } = await validateForm();
    if (!formValid) {
      api.error({
        message: 'Erreur de validation',
        description: 'Veuillez renseigner les champs manquants.',
        placement: 'bottomRight',
      });
      return;
    }

    if (selectedField.transformation_type === 'chain') {
      const chain = selectedField.transformation?.chain;

      // 1. At least 2 steps
      if (!chain || !chain.steps || chain.steps.length < 1) {
        api.error({
          message: 'Erreur de validation',
          description:
            'La chaîne de transformations doit contenir au moins 1 étape.',
          placement: 'bottomRight',
        });
        return;
      }
    }

    // VÉRIFICATION TRADUCTIONS EN BASE
    const translationDbFields = findTranslateDbFields(selectedField);
    if (translationDbFields.length > 0) {
      const isPlural = translationDbFields.length > 1;
      const fieldLabel = isPlural ? 'Les champs' : 'Le champ';
      const verbForm = isPlural ? 'disposent' : 'dispose';

      const confirmed = await new Promise((resolve) => {
        Modal.confirm({
          title: 'Traductions existantes en base de données',
          content: `${fieldLabel} "${translationDbFields.join(', ')}" ${verbForm} déjà de traductions en base pour certains produits. Merci d'annuler et d'utiliser un mapping simple. Voulez-vous quand même continuer ?`,
          okText: 'Continuer',
          cancelText: 'Annuler',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
    }

    // UPDATE MAPPING DETAILS WITH MODIFIED FIELD
    const updatedMappingDetails = updateMappingWithField(
      mappingDetails,
      selectedField
    );

    // // Update the store with the new mapping details
    // setMappingDetails(updatedMappingDetails);
    try {
      const response = await updateMapping({
        id: mappingDetails.id,
        body: updatedMappingDetails,
      });
      navigate(`/dashboard/mapping/${response.id}`);
      api.success({
        message: 'Mapping mis à jour',
        description: 'Le mapping a été mis à jour avec succès.',
        placement: 'bottomRight',
      });
      handleClose();
    } catch (error) {
      const data = error?.response?.data;
      const description = Array.isArray(data)
        ? data.join(' ')
        : data?.message ||
          data?.detail ||
          (typeof data === 'string' ? data : null) ||
          'Une erreur est survenue lors de la sauvegarde du mapping.';
      api.error({
        message: 'Erreur',
        description,
        placement: 'bottomRight',
      });
    }
  };

  useEffect(() => {
    if (triggerOpen) {
      setIsOpen(true);
    }
  }, [triggerOpen]);

  return (
    <>
      {contextHolder}
      <Drawer
        closable={false}
        zIndex={1000}
        title={
          <div translate="no">
            {/* Évite la traduction navigateur sur la barre d’actions (cohérent avec le corps du drawer). */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Button type="link" icon={<LeftOutlined />} onClick={handleClose}>
                Retour
              </Button>
              <Space>
                <Button type="default" disabled>
                  Besoin d&apos;aide ?
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={isUpdating}
                  onClick={handleSave}
                >
                  Enregistrer
                </Button>
              </Space>
            </div>
          </div>
        }
        width="calc(100vw - 220px)"
        onClose={handleClose}
        destroyOnHidden={true}
        open={isOpen}
      >
        {/* Formulaire mapping : pas de traduction navigateur sur l’arbre DOM (réconciliation React). */}
        <div translate="no">
          <MappingForm />
        </div>
      </Drawer>
    </>
  );
};

export default MappingConfigurator;
